import { PrimitiveAtom, useAtom } from 'jotai'
import { useEffect, useState } from 'react'

import { deleteFile, searchDocsAtom } from '@/store/documents/DocumentsAtom'
import {
    chatIdAtom,
    chatSettingsAtom,
    checkIfDocumentsAreReady,
    getChatSettings,
    newQuestionAtom,
    postChatSettings,
    saveSettings,
} from '@/store/gigiChat/ChatAtom'
import { IConversationSettings, IDocument } from '@/types/gigiChat/ConversationSettings'
import { deepClone } from '@/utils'

import UploadedFiles from './UploadedFiles'

const Container = () => {
    const [chatSettings] = useAtom<PrimitiveAtom<IConversationSettings | null>>(chatSettingsAtom)
    const [chatId] = useAtom<PrimitiveAtom<string>>(chatIdAtom)
    const [searchWord] = useAtom<PrimitiveAtom<string>>(searchDocsAtom)
    const [, setQuestion] = useAtom<PrimitiveAtom<string | null>>(newQuestionAtom)
    const [filteredFiles, setfilteredFiles] = useState<IDocument[]>([])
    const [itemLoadingDeleteIndex, setItemLoadingDeleteIndex] = useState<number | null>(null)

    useEffect(() => {
        let intervalId: ReturnType<typeof setTimeout> | null = null

        async function getSettings() {
            const settings = (await getChatSettings(chatId)) as IConversationSettings
            settings.processingStrategy = 'DocAnalysis'
            saveSettings(settings)
        }

        function checkDocumentStatus() {
            const areDocumentReady = checkIfDocumentsAreReady()

            if (areDocumentReady && areDocumentReady.length > 0) {
                if (!intervalId) {
                    intervalId = setInterval(() => {
                        void getSettings()
                    }, 2000)
                }
            } else if (areDocumentReady && areDocumentReady.length === 0 && intervalId) {
                clearInterval(intervalId)
                intervalId = null
            }
        }

        checkDocumentStatus()

        return () => {
            if (intervalId) {
                clearInterval(intervalId)
            }
        }
    }, [chatId, chatSettings])

    useEffect(() => {
        const localSettings = deepClone<object>(chatSettings || {}) as IConversationSettings
        if (localSettings && localSettings.processingStrategyDocAnalysis && localSettings.processingStrategyDocAnalysis.docs) {
            if (searchWord) {
                const filteredList = localSettings.processingStrategyDocAnalysis.docs.filter((item) => {
                    const fileName = item.name.toLowerCase()
                    return fileName.includes(searchWord)
                })

                setfilteredFiles(filteredList)
            } else {
                setfilteredFiles(localSettings.processingStrategyDocAnalysis.docs)
            }
        }
    }, [setfilteredFiles, chatSettings, searchWord])

    const onItemClick = (selected: boolean, index: number) => {
        const localSettings = deepClone<object>(chatSettings || {}) as IConversationSettings
        let docs = localSettings.processingStrategyDocAnalysis.docs

        if (docs) {
            docs = docs.map((item, position) => {
                if (position === index) {
                    item.selected = selected
                }

                return item
            })

            localSettings.processingStrategyDocAnalysis.docs = docs

            void postChatSettings(chatId, localSettings, true).then((results) => {
                if (results && results.ok) {
                    saveSettings(localSettings)
                }
            })
        }
    }

    const onSelectAll = (isSelectAll: boolean) => {
        const localSettings = deepClone<object>(chatSettings || {}) as IConversationSettings
        let docs = localSettings.processingStrategyDocAnalysis.docs

        if (docs) {
            docs = docs.map((item) => {
                item.selected = isSelectAll

                return item
            })

            localSettings.processingStrategyDocAnalysis.docs = docs

            void postChatSettings(chatId, localSettings, true).then((results) => {
                if (results && results.ok) {
                    saveSettings(localSettings)
                }
            })
        }
    }

    const onDelete = (fileName: string, index: number) => {
        setItemLoadingDeleteIndex(index)
        const deleteFileItem = async () => {
            await deleteFile(fileName).then(async (result) => {
                if (result && result.ok) {
                    const settings = (await getChatSettings(chatId)) as IConversationSettings
                    saveSettings(settings)
                }
                setItemLoadingDeleteIndex(null)
            })
        }
        void deleteFileItem()
    }

    if (chatSettings && chatSettings.processingStrategyDocAnalysis.docs.length === 0) {
        return null
    }

    return (
        <UploadedFiles
            files={filteredFiles}
            onItemClick={onItemClick}
            onSelectAll={onSelectAll}
            onDelete={onDelete}
            loadingDeleteIndex={itemLoadingDeleteIndex}
            onSumarize={setQuestion}
        />
    )
}

export default Container
